import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadPrescriberDetails } from '../../../store/directusService';
import {
    clearBSOPDetails,
    loadBSOPDetails,
    updateBSOPDetails
} from '../../../store/participantService';
import { getObjFromId } from '../../../utils/directusFunctions';
import { EDIT_ON_CLOSED_ROLES } from '../../../utils/formValidation/commonConstants';
import { NO_CURRENT_PARTICIPANT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { NO_PRESCRIBER_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { trimEntries } from '../../../utils/objectUtils';
import { ADVISER, hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateBSOP';

import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [ADVISER, MANAGER, QUALITY, SUPERUSER];
const _UPDATE_MESSAGE = 'Participant BSOP details have been updated';

const BSOP = () => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const initialState = {
        id: '',
        participantId: '',
        prescriberId: ''
    };
    const [newEntry, setNewEntry] = useState(initialState);
    const [initialStateOnEntry, setInitialStateOnEntry] = useState(initialState);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState(false);
    const [arrayPrescribers, setArrayPrescribers] = useState([]);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const bsopDetails = useSelector((state) => state.entities.participantService.bsopDetails);
    const prescriberDetails = useSelector(
        (state) => state.entities.directusService.prescriberDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        prescriberDetails?.length < 1 && dispatch(loadPrescriberDetails());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (prescriberDetails?.length < 1) return;
        setArrayPrescribers(
            prescriberDetails.map((el) => ({
                ...el,
                realName: el.name,
                name: `${el.name} (${el.organisational_code})`
            }))
        );
    }, [prescriberDetails]);

    useEffect(() => {
        if ('id' in currentParticipant && !newEntry.participantId)
            dispatch(loadBSOPDetails(currentParticipant.id));
    }, [currentParticipant?.id]);

    useEffect(() => {
        if ('status' in currentParticipant) {
            const disableOnClosedStatus =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(!hasRole(acceptedRoles, roles) || disableOnClosedStatus);
        }
    }, [currentParticipant?.status, roles]);

    useEffect(() => {
        if (bsopDetails?.participantId !== currentParticipant?.id) {
            setNewEntry((prev) => ({ ...prev, participantId: currentParticipant?.id }));
        }
        if (Object.keys(bsopDetails)?.length) {
            const updated = trimEntries('prescriber', bsopDetails);
            setNewEntry(updated);
            setInitialStateOnEntry(updated);
        }
    }, [bsopDetails, currentParticipant?.id]);

    useEffect(() => {
        setIsUpdateButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage === _UPDATE_MESSAGE) setIsUpdateButtonDisabled(false);
    }, [successMessage]);

    // HELPER FNS
    const loadingError = () => {
        if (Object.keys(currentParticipant)?.length < 1) return NO_CURRENT_PARTICIPANT_ERROR;
        if (prescriberDetails?.length < 1) return NO_PRESCRIBER_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const cleanUp = () => dispatch(clearBSOPDetails());

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsUpdateButtonDisabled(true);
        const newErrors = validate(newEntry, initialStateOnEntry);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(updateBSOPDetails(newEntry, _UPDATE_MESSAGE));
    };

    // RENDER
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <div>
            <div className={form.formWrapper}>
                <form onSubmit={onSubmit} data-testid="form_start_bsop" className={form.form}>
                    <div className={form.formSection}>
                        <div className={form.formColumn}>
                            <SingleSelect
                                id="prescriberId"
                                label="Prescriber"
                                placeholder="Select a prescriber..."
                                disabled={isDisabled}
                                menuItems={arrayPrescribers || []}
                                selectedId={newEntry.prescriberId || ''}
                                selected={getObjFromId(arrayPrescribers, newEntry.prescriberId)}
                                onChange={(chosenId) => onUpdate('prescriberId', chosenId)}
                            />
                        </div>
                        <div className={form.formColumn}></div>
                    </div>

                    <Button
                        id="bsopUpdateButton"
                        disabled={isDisabled || isUpdateButtonDisabled}
                        content="Update BSOP"
                        error={errors.button}
                        clearError={() => clearError('button')}
                    />
                </form>
            </div>
        </div>
    );
};

export default BSOP;
